import React, { useCallback } from 'react';
import { Statistic, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import styles from './index.module.less';
import Button from '../Button/Button';
import { MIN_WALLET_BALANCE } from '../../utils/constants';
import { UserBalanceModel } from '../../api/models/payments';
import useAnalytika, { getAnalytikaScreenName } from '../../utils/analytika/reporterAnalytika';
import { walletWidgetRechargeClicked } from '../../utils/analytika/events';
import { useLocation } from 'react-router-dom';
import { colorError, colorWarning } from '../../styles/theme';
import classnames from 'classnames';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';

type WalletBalanceWidgetProps = {
  isLoading: boolean;
  openAmountModal: () => void;
} & UserBalanceModel;

export enum WidgetState {
  NEGATIVE = 'negative',
  LOW = 'low',
  EMPTY = 'empty',
  NORMAL = 'normal'
}

const WalletBalanceWidget = ({
  amount = 0,
  currency,
  fractionDigits,
  openAmountModal
}: WalletBalanceWidgetProps): JSX.Element => {
  const { t } = useTranslation();
  const { fireEvent } = useAnalytika();
  const { pathname } = useLocation();
  const screenName = getAnalytikaScreenName(pathname);

  const title = t('widgets.walletBalance.title', { currency });
  let widgetState: WidgetState;
  let tagBg = '';

  if (amount < 0) {
    widgetState = WidgetState.NEGATIVE;
    tagBg = colorError;
  } else if (amount === 0) {
    widgetState = WidgetState.EMPTY;
    tagBg = colorError;
  } else if (amount > 0 && amount < MIN_WALLET_BALANCE) {
    widgetState = WidgetState.LOW;
    tagBg = colorWarning;
  } else {
    widgetState = WidgetState.NORMAL;
  }

  const openModal = useCallback(() => {
    fireEvent(
      walletWidgetRechargeClicked(screenName, {
        wallet_widget_tag: widgetState,
        wallet_balance: +amount
      })
    );
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_RECHARGE_WALLET,
      event_version: 2,
      wallet_balance: amount,
      wallet_currency: currency
    });
    openAmountModal();
  }, [openAmountModal, widgetState, currency]);

  const tagDisplayed: React.ReactNode = widgetState !== WidgetState.NORMAL && (
    <Tag color={tagBg} className={styles.tag}>
      {t(`widgets.walletBalance.${widgetState}`)}{' '}
    </Tag>
  );
  return (
    <div className={classnames([styles.widgetContainer, styles[widgetState]])}>
      <Statistic
        valueRender={value => <Typography.Title level={2}>{value}</Typography.Title>}
        title={title}
        value={amount}
        precision={fractionDigits}
        suffix={tagDisplayed}
      />
      <Button size={'large'} type={'primary'} onClick={openModal} className={styles.rechargeButton}>
        {t('widgets.walletBalance.recharge')}
      </Button>
    </div>
  );
};

export default WalletBalanceWidget;
